import { defineStore } from "pinia";
import { ref, computed } from "vue";
import apiService from "./utilities/apiService";
import EmptyStarImage from "../../assets/stars/emptyStar.png";
import HalfStarImage from "../../assets/stars/halfStar.png";
import FullStarImage from "../../assets/stars/fullStar.png";
import { emptyString, getDefaultFilter, modelFilterEnum, surveyTypeFilterEnum, starTypeEnum, } from "./utilities/helpers";
// There are two types of Pinia stores - Setup Store and Options Store
// Setup stores bring a lot more flexibility than Option Stores as
// you can create watchers within a store and freely use any composable.
// However, keep in mind that using composables will get more complex when using SSR.
// https://pinia.vuejs.org/core-concepts/#what-syntax-should-i-pick
export const useReviewsStore = defineStore("reviewsStore", () => {
    // STATE PROPERTIES - ref()s become state properties
    const dealerId = ref(0);
    const dealerInfo = ref({
        averageRating: 0,
        dealerCode: "",
        numberOfRatings: 0,
    });
    const dealerDetails = ref();
    const dealerName = ref("");
    const pages = ref([[]]);
    const dataVersion = ref(emptyString);
    const defaultSelectedFilter = getDefaultFilter();
    const selectedFilter = ref(defaultSelectedFilter);
    const currentPage = ref(1);
    const reviewsPerPage = 50;
    const totalReviews = ref(0);
    const totalReviewsAverage = ref("");
    // GETTERS - computed()s become getters
    const getDataVersion = computed(() => dataVersion.value);
    const getDealerInfo = computed(() => dealerInfo.value);
    const getDealerDetails = computed(() => dealerDetails);
    const getCustomerReviews = computed(() => {
        return pages.value;
    });
    const getTotalPages = computed(() => pages.value.length - 1);
    const getFilteredReviewsAverage = computed(() => {
        /*Sales and Service reviews should be split 50/50 to calculate the average score*/
        let rawAverage = 0;
        let filteredReviewsAverage = "";
        const hasReviews = pages.value.length > 1 && pages.value[1].length > 0;
        const serviceReviews = {
            ratingsTotal: 0,
            ratingsCount: 0,
            average: 0,
        };
        const salesReviews = {
            ratingsTotal: 0,
            ratingsCount: 0,
            average: 0,
        };
        if (hasReviews) {
            pages.value.forEach((page) => {
                page.forEach((reviewComment) => {
                    if (reviewComment.category === surveyTypeFilterEnum.Sales.toString()) {
                        salesReviews.ratingsCount++;
                        salesReviews.ratingsTotal += parseInt(reviewComment.rating);
                    }
                    else {
                        serviceReviews.ratingsCount++;
                        serviceReviews.ratingsTotal += parseInt(reviewComment.rating);
                    }
                });
            });
            salesReviews.average =
                salesReviews.ratingsCount > 0
                    ? salesReviews.ratingsTotal / salesReviews.ratingsCount
                    : 0;
            serviceReviews.average =
                serviceReviews.ratingsCount > 0
                    ? serviceReviews.ratingsTotal / serviceReviews.ratingsCount
                    : 0;
            if (salesReviews.average > 0 && serviceReviews.average) {
                rawAverage = (salesReviews.average + serviceReviews.average) / 2;
            }
            else if (salesReviews.average > 0) {
                rawAverage = salesReviews.average;
            }
            else if (serviceReviews.average > 0) {
                rawAverage = serviceReviews.average;
            }
            filteredReviewsAverage = rawAverage.toFixed(4).toString();
        }
        if (totalReviewsAverage.value.length < 1 &&
            totalReviews.value === dealerDetails.value?.numberOfRatings) {
            totalReviewsAverage.value = filteredReviewsAverage;
        }
        return filteredReviewsAverage;
    });
    const getFilteredReviewsCount = computed(() => totalReviews.value);
    const getmodelFilters = () => Object.values(modelFilterEnum);
    const getSurveyTypeFilters = () => Object.values(surveyTypeFilterEnum);
    // ACTIONS - function()s become actions
    async function loadDataVersion() {
        try {
            const response = await apiService.getDataVersion();
            if (response) {
                dataVersion.value = response;
            }
        }
        catch (error) {
            console.error(error);
        }
    }
    async function loadDealerInfo() {
        try {
            await apiService
                .getDealerInfo(dataVersion.value, dealerId.value)
                .then((response) => {
                if (response) {
                    dealerInfo.value = response;
                }
            });
        }
        catch (error) {
            console.error(error);
        }
    }
    async function loadDealerDetails() {
        try {
            await apiService
                .getDealerDetails(dataVersion.value, dealerId.value)
                .then((response) => {
                if (response) {
                    dealerDetails.value = response;
                }
            });
        }
        catch (error) {
            console.error(error);
        }
    }
    async function loadCustomerReviews() {
        try {
            const customerReviewsApiParams = {
                category: selectedFilter.value.surveyType === surveyTypeFilterEnum.Overall
                    ? ""
                    : selectedFilter.value.surveyType,
                dataVersion: dataVersion.value,
                dealerId: dealerId.value,
                models: selectedFilter.value.models,
                pageOffet: 100,
                ratings: selectedFilter.value.ratings,
                recordLimit: 250000,
                years: [],
            };
            await apiService
                .getCustomerReviews(customerReviewsApiParams)
                .then((response) => {
                if (response) {
                    const customerReviews = response;
                    totalReviews.value = customerReviews.length;
                    pages.value = [[]];
                    let remainingComments = customerReviews.length;
                    let startIndex = 0;
                    let endIndex = reviewsPerPage;
                    do {
                        pages.value.push(customerReviews.slice(startIndex, endIndex));
                        startIndex += reviewsPerPage;
                        endIndex += reviewsPerPage;
                        remainingComments -= reviewsPerPage;
                    } while (remainingComments > 0);
                    currentPage.value = 1;
                    if (totalReviews.value === dealerDetails.value?.numberOfRatings) {
                        totalReviewsAverage.value = getFilteredReviewsAverage.value;
                    }
                }
            });
        }
        catch (error) {
            console.error(error);
        }
    }
    function processSelectedFilter() {
        loadCustomerReviews();
    }
    function nextPage() {
        if (currentPage.value !== getTotalPages.value) {
            currentPage.value = currentPage.value + 1;
        }
    }
    function previousPage() {
        if (currentPage.value !== 1) {
            currentPage.value = currentPage.value - 1;
        }
    }
    function getTotalReviewsAverageStars() {
        const ratingIntegerAndDecimal = totalReviewsAverage.value.split(".");
        const numberFullStars = parseInt(ratingIntegerAndDecimal[0]);
        const decimalValue = parseInt(ratingIntegerAndDecimal[1]);
        /* This calculation formula came from Subaru */
        let calculatedStar = starTypeEnum.Full;
        if (decimalValue >= 0 && decimalValue <= 3999) {
            calculatedStar = starTypeEnum.Empty;
        }
        else if (decimalValue >= 4000 && decimalValue <= 8499) {
            calculatedStar = starTypeEnum.Half;
        }
        // default review is 5 stars
        const stars = {
            starOneImagePath: FullStarImage,
            starTwoImagePath: FullStarImage,
            starThreeImagePath: FullStarImage,
            starFourImagePath: FullStarImage,
            starFiveImagePath: FullStarImage,
        };
        const starsKeys = Object.keys(stars);
        // if less than 5 full stars, use the calculated star - half or empty
        if (numberFullStars <= 5) {
            const starKey = starsKeys[numberFullStars];
            stars[starKey] = getStarImage(calculatedStar);
        }
        // if less than 4 full stars, fill with empty stars
        for (let x = numberFullStars + 1; x <= 5; x++) {
            const starKey = starsKeys[x];
            stars[starKey] = EmptyStarImage;
        }
        return stars;
    }
    function getStarImage(starType) {
        if (starType === starTypeEnum.Full) {
            return FullStarImage;
        }
        else if (starType === starTypeEnum.Empty) {
            return EmptyStarImage;
        }
        else {
            return HalfStarImage;
        }
    }
    return {
        // State
        dealerId,
        dealerName,
        currentPage,
        pages,
        selectedFilter,
        totalReviewsAverage,
        // Getters
        getDataVersion,
        getDealerInfo,
        getDealerDetails,
        getCustomerReviews,
        getFilteredReviewsAverage,
        getFilteredReviewsCount,
        getTotalPages,
        getmodelFilters,
        getSurveyTypeFilters,
        // Actions
        loadDataVersion,
        loadDealerInfo,
        loadDealerDetails,
        loadCustomerReviews,
        processSelectedFilter,
        nextPage,
        previousPage,
        getTotalReviewsAverageStars,
    };
});
